import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Divider, Typography } from "@material-ui/core";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
} from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
  editorContainer: {
    border: "1px solid #ccc",
    borderRadius: "4px",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    padding: theme.spacing(1),
    borderBottom: "1px solid #eee",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  toolbarGroup: {
    display: "flex",
    gap: theme.spacing(0.25),
  },
  editorContent: {
    minHeight: "150px",
    maxHeight: "500px",
    overflow: "auto",
    padding: theme.spacing(1, 2),
    "& .ProseMirror": {
      outline: "none",
      "& p": {
        margin: "0.5em 0",
      },
      "& ul, ol": {
        paddingLeft: "1.5em",
      },
      "& blockquote": {
        borderLeft: "3px solid #ccc",
        paddingLeft: "1em",
        margin: "1em 0",
        fontStyle: "italic",
      },
      "& h1, h2, h3, h4, h5, h6": {
        fontWeight: "bold",
        margin: "1em 0 0.5em 0",
      },
      "& h1": { fontSize: "2em" },
      "& h2": { fontSize: "1.5em" },
      "& h3": { fontSize: "1.25em" },
      "& table": {
        borderCollapse: "collapse",
        width: "100%",
        margin: "1em 0",
      },
      "& td, th": {
        border: "1px solid #ccc",
        padding: "0.5em",
      },
      "& th": {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
      },
    },
  },
  activeButton: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

interface TiptapRichTextInputProps {
  source: string;
  initialValue?: string;
  validate?: any[];
  onChange?: (value: string) => void;
  value?: string;
}

const TiptapRichTextInput: React.FC<TiptapRichTextInputProps> = ({
  source,
  initialValue = "",
  validate,
  onChange,
  value,
}) => {
  const classes = useStyles();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      FontFamily,
    ],
    content: initialValue || value || "<p></p>",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && initialValue && initialValue !== editor.getHTML()) {
      editor.commands.setContent(initialValue);
    }
  }, [initialValue, editor]);

  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt("URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <Button
      size="small"
      onClick={onClick}
      className={isActive ? classes.activeButton : ""}
      title={title}
      variant={isActive ? "contained" : "outlined"}
      style={{ minWidth: "36px", padding: "4px 8px" }}
    >
      {children}
    </Button>
  );

  return (
    <Box className={classes.editorContainer}>
      <Box className={classes.toolbar}>
        <Box className={classes.toolbarGroup}>
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} isActive={false} title="Undo">
            <Undo fontSize="small" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} isActive={false} title="Redo">
            <Redo fontSize="small" />
          </ToolbarButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box className={classes.toolbarGroup}>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
          >
            <Typography variant="caption">H1</Typography>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
          >
            <Typography variant="caption">H2</Typography>
          </ToolbarButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box className={classes.toolbarGroup}>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title="Bold"
          >
            <FormatBold fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title="Italic"
          >
            <FormatItalic fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
            title="Underline"
          >
            <FormatUnderlined fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive("strike")}
            title="Strikethrough"
          >
            <FormatStrikethrough fontSize="small" />
          </ToolbarButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box className={classes.toolbarGroup}>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title="Bullet List"
          >
            <FormatListBulleted fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title="Numbered List"
          >
            <FormatListNumbered fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive("blockquote")}
            title="Blockquote"
          >
            <FormatQuote fontSize="small" />
          </ToolbarButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box className={classes.toolbarGroup}>
          <ToolbarButton onClick={setLink} isActive={editor.isActive("link")} title="Add Link">
            <LinkIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton onClick={addImage} isActive={false} title="Add Image">
            <ImageIcon fontSize="small" />
          </ToolbarButton>
        </Box>
      </Box>

      <EditorContent editor={editor} className={classes.editorContent} />
    </Box>
  );
};

export default TiptapRichTextInput;
